import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';

import { Tooltip } from '@/components/Tooltip';
import { cn } from '@/utils/classname';

/**
 * TableSelectionBar displays a fixed-height selection bar with bulk actions
 * and selection count information for table rows.
 *
 * This component provides a consistent selection UI across all table pages
 * in the Chariot platform, with pluggable actions via render props.
 *
 * **Key Features:**
 * - Fixed height (60px) to prevent layout shift
 * - Automatic visibility based on selection state
 * - Optional always-visible mode with disabled state when no selection
 * - Vertical layout with actions on top, info below
 * - Theme-aware styling with Chariot design system
 *
 * **Usage Pattern:**
 * By default, only renders when rows are selected. With `alwaysVisible={true}`,
 * the bar remains mounted but shows disabled state when no selection.
 *
 * @example
 * ```tsx
 * <TableSelectionBar
 *   alwaysVisible={true}  // NEW - Always show bar
 *   isVisible={selectedKeys.length > 0}
 *   selectedCount={selectedKeys.length}
 *   totalCount={data.length}
 *   hasNextPage={hasNextPage}
 *   emptySelectionMessage="Select items to perform bulk actions"
 *   actions={
 *     <AssetActions
 *       assets={selectedAssets}
 *       onSuccess={clearSelection}
 *       // ... other props
 *     />
 *   }
 * />
 * ```
 */
export interface TableSelectionBarProps {
  /**
   * Whether the selection bar should be visible
   * Typically: selectedKeys.length > 0 or isRowsSelected
   * Note: Ignored when alwaysVisible is true
   * @required
   */
  isVisible: boolean;

  /**
   * Always show the bar, but disable actions when no selection
   * When true, ignores `isVisible` and renders bar in disabled state
   * when selectedCount is 0. Prevents layout shift and provides visual
   * feedback that bulk actions are available.
   * @default false
   */
  alwaysVisible?: boolean;

  /**
   * Optional message to show when no items are selected
   * Only used when alwaysVisible=true and selectedCount=0
   * @default "No {resourceLabel} selected"
   */
  emptySelectionMessage?: string;

  /**
   * Number of items currently selected
   * @required
   */
  selectedCount: number;

  /**
   * Total number of items currently loaded/visible
   * @required
   */
  totalCount: number;

  /**
   * Whether more data is available via infinite scroll
   * Affects the selection message wording
   * @default false
   */
  hasNextPage?: boolean;

  /**
   * Pre-formatted label for the total database count display.
   * When provided, appended to the selection message after a bullet separator.
   * Example: "150 Open vulnerabilities" or "1,200 total assets"
   * @optional
   */
  totalDatabaseLabel?: string;

  /**
   * Label for the resource type (e.g., "assets", "seeds", "technologies")
   * Used in the selection message for better context
   * @default "items"
   */
  resourceLabel?: string;

  /**
   * Custom action components to render in the actions area
   * Typically: bulk action dropdowns or buttons
   * @required
   */
  actions: React.ReactNode;

  /**
   * Optional content to render on the right side of the action bar
   * Typically: search input or additional controls
   * Positioned after actions with ml-auto for right alignment
   */
  rightContent?: React.ReactNode;

  /**
   * Optional CSS class name for styling overrides
   */
  className?: string;

  /**
   * Optional test ID for E2E testing
   */
  'data-testid'?: string;

  /**
   * When true, removes border/background styling for use inside containers
   * like TableControlsContainer. The container provides the visual styling,
   * so this component should not duplicate it.
   * @default false
   */
  embedded?: boolean;

  /**
   * When true, the user has promoted selection to "all matching" mode
   * (Gmail-style step 2 — see SelectionBanner). The bar then shows
   * "All matching {resourceLabel} selected." instead of an "X of Y" count,
   * since the actual count is unknown until the bulk job completes.
   * @default false
   */
  isSelectAll?: boolean;
}

/**
 * Generate selection message with visible total count
 *
 * Returns both the primary message (now includes total count) and tooltip.
 * Handles both normal selection state and empty selection state (for alwaysVisible mode).
 */
function getSelectionInfo(
  selectedCount: number,
  totalCount: number,
  hasNextPage: boolean,
  resourceLabel: string = 'items',
  emptySelectionMessage?: string,
  totalDatabaseLabel?: string,
  isSelectAll: boolean = false
): { message: string; tooltip: string } {
  // Handle empty selection state (for alwaysVisible mode)
  if (selectedCount === 0) {
    const message = emptySelectionMessage || `No ${resourceLabel} selected`;
    const tooltip = 'Select items from the table to enable bulk actions';
    return { message, tooltip };
  }

  // "Select all matching" mode — actual count isn't known until bulk job runs.
  if (isSelectAll) {
    let message = `All matching ${resourceLabel} selected`;
    if (totalDatabaseLabel) {
      message += ` • ${totalDatabaseLabel}`;
    }
    message += '.';
    return {
      message,
      tooltip:
        'Actions will be applied to all items matching your current filters.',
    };
  }

  const isAllSelected = selectedCount === totalCount;

  // Primary message with total count visible
  let message: string;

  // Build base message
  if (isAllSelected && hasNextPage) {
    message = `All ${selectedCount} ${resourceLabel} on this page are selected`;
  } else if (isAllSelected && !hasNextPage) {
    message = `All ${selectedCount} ${resourceLabel} are selected`;
  } else {
    message = `${selectedCount} of ${totalCount} ${resourceLabel} selected`;
  }

  // Append total database label if provided
  if (totalDatabaseLabel) {
    message += ` • ${totalDatabaseLabel}`;
  }

  message += '.';

  // Tooltip
  const tooltip =
    'Actions will be applied to the selected items currently loaded on this page. To select more items, load additional pages using infinite scroll.';

  return { message, tooltip };
}

/**
 * TableSelectionBar Component
 *
 * Provides consistent selection bar UI across Assets, Seeds, and Technologies tables.
 * Uses render props pattern for pluggable action components.
 * Supports always-visible mode with disabled state when no selection.
 */
export function TableSelectionBar({
  isVisible,
  alwaysVisible = false,
  emptySelectionMessage,
  selectedCount,
  totalCount,
  hasNextPage = false,
  totalDatabaseLabel,
  resourceLabel = 'items',
  actions,
  rightContent,
  className,
  'data-testid': dataTestId = 'table-selection-bar',
  embedded = false,
  isSelectAll = false,
}: TableSelectionBarProps) {
  // Calculate visibility and disabled state
  const shouldShow = alwaysVisible || isVisible;
  const isDisabled = alwaysVisible && selectedCount === 0;

  // Memoize message and tooltip generation
  const { message, tooltip } = useMemo(
    () =>
      getSelectionInfo(
        selectedCount,
        totalCount,
        hasNextPage,
        resourceLabel,
        emptySelectionMessage,
        totalDatabaseLabel,
        isSelectAll
      ),
    [
      selectedCount,
      totalCount,
      hasNextPage,
      resourceLabel,
      emptySelectionMessage,
      totalDatabaseLabel,
      isSelectAll,
    ]
  );

  // Compute text/icon colors based on disabled state
  const textColorClass = isDisabled
    ? 'text-chariot-text-tertiary'
    : 'text-chariot-text-secondary';
  const iconColorClass = isDisabled
    ? 'text-chariot-text-tertiary'
    : 'text-chariot-text-secondary';

  // Always render container with fixed height to prevent layout shift
  // When not visible, just render empty container to maintain spacing
  if (!shouldShow) {
    return (
      <div
        className={cn(
          'flex min-h-[60px] items-center',
          !embedded && 'mb-4',
          className
        )}
        data-testid={`${dataTestId}-container`}
      />
    );
  }

  return (
    <div
      className={cn(
        'flex min-h-[60px] items-center',
        !embedded && 'mb-4',
        className
      )}
      data-testid={`${dataTestId}-container`}
    >
      <div
        className={cn(
          'flex w-full flex-col gap-2 px-4 py-3',
          !embedded &&
            'border-chariot-stroke bg-chariot-background-secondary rounded-md border'
        )}
        data-testid={dataTestId}
        aria-disabled={isDisabled}
      >
        {/* Row 1: Actions and optional right content */}
        <div
          className="flex w-full flex-wrap items-center justify-between gap-2"
          data-testid={`${dataTestId}-actions`}
        >
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
          {rightContent && <div className="shrink-0">{rightContent}</div>}
          {/* Selection info on right (when no rightContent) */}
          {!rightContent && (
            <div
              className="flex items-center gap-2"
              data-testid={`${dataTestId}-info`}
            >
              <p className={cn('text-sm', textColorClass)}>{message}</p>
              <Tooltip placement="top" title={tooltip}>
                <InformationCircleIcon
                  className={cn('size-4 shrink-0', iconColorClass)}
                  aria-label="Selection information"
                />
              </Tooltip>
            </div>
          )}
        </div>

        {/* Row 2: Selection info (only when rightContent is provided - legacy mode) */}
        {rightContent && (
          <div
            className="flex items-center gap-2"
            data-testid={`${dataTestId}-info`}
          >
            <p className={cn('text-sm', textColorClass)}>{message}</p>
            <Tooltip placement="top" title={tooltip}>
              <InformationCircleIcon
                className={cn('size-4 shrink-0', iconColorClass)}
                aria-label="Selection information"
              />
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}
